import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import { BuddyPageSignup } from '@adornis/bh-matching-signup/components/buddy-page-signup.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
import '@adornis/buchungsheld-theme/components/tr-icon';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { XImage } from '@adornis/file-utils/client/x-image.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { MentoringMentor } from '../../entities/mentoring-user.js';
import { mentorCustomFieldMapping } from '../form-mapping.js';

@customElement('mentoring-mentor-signup')
export class MentoringMentorSignup extends BuddyPageSignup {
  @property({ attribute: 'description-translate-key' }) descTranslKey?: string = 'field_description';

  private imageRef: Ref<XImage> = createRef();

  override customFieldMapping = mentorCustomFieldMapping;
  override userClass = MentoringMentor;

  protected override _personalDataContent() {
    const fields = Object.values(this.userClass.value?.fields ?? {});

    const personalDataFields = [
      'title',
      'firstName',
      'lastName',
      'gender',
      'birthday',
      'age',
      'email',
      'emailRepeat',
      'phone',
      'address',
      'privacyPolicy',
    ];

    const additionalDataFields = [
      'mentorSprache',
      'studium',
      'abschluss',
      'currentPosition',
      'currentOcupationFocus',
      'berufserfahrung',
      'sonstigesErfahrungen',
      'genderPreference',
      'meetingType',
      'note',
      'motivationMentor',
      'menteeDepartments',
      'maxParallelMentees',
      'international',
      'internationalMentees',
      'goetheStudent',
      'businessProfile',
      'abschlussarbeitBetreuen',
      'workIntegratedLeaning',
      'discoveredFrom',
    ];

    return html`
      <tr-card ${css({ width: '100%' })}>
        <tr-h3> ${this._translation.translate('signup_mentee', 'profile_image')} </tr-h3>
        <tr-flex horizontal crossaxis-center space="md">
          <tr-icon ${css({ height: '9px', color: this.colors.neutral.shade(50) })}> info </tr-icon>
          <tr-text> ${this._translation.translate('signup_mentee', 'profile_image_info')} </tr-text>
        </tr-flex>
        <tr-flex center crossaxis-center>
          <tr-image
            ${this._form.field('profilePicture')}
            mode="cover"
            ${css({ width: '200px', height: '200px', borderRadius: '50%', overflow: 'hidden' })}
            clearable
            ${ref(this.imageRef)}
          ></tr-image>
          <tr-button
            mode="icon"
            icon="close"
            @click=${() => {
              if (!this._form.document) return;
              if (!this.imageRef.value) return;
              this._form.document.profilePicture = undefined;
              this.imageRef.value.clear();
              this.requestUpdate();
            }}
          ></tr-button>
        </tr-flex>
      </tr-card>
      <tr-card ${css({ width: '100%' })}>
        <tr-h3> ${this._translation.translate('signup_mentee', 'personal_data')} </tr-h3>
        <tr-grid columns="1fr 2fr" space="md">
          ${this._fields(
            fields
              .filter(field => personalDataFields.includes(field.name))
              .sort((a, b) => personalDataFields.indexOf(a.name) - personalDataFields.indexOf(b.name)),
          )}
        </tr-grid>
      </tr-card>
      <tr-card ${css({ width: '100%' })}>
        <tr-h3> ${this._translation.translate('signup_mentee', 'additional_data')} </tr-h3>
        <tr-grid columns="1fr 2fr" space="md">
          ${this._fields(
            fields
              .filter(field => additionalDataFields.includes(field.name))
              .sort((a, b) => additionalDataFields.indexOf(a.name) - additionalDataFields.indexOf(b.name)),
          )}
        </tr-grid>
      </tr-card>
      <tr-card ${css({ width: '100%' })}>
        <tr-h3> ${this._translation.translate('signup_mentee', 'cv')} </tr-h3>
        <tr-text> ${this._translation.translate('signup_mentee', 'cv_info')} </tr-text>
        <tr-file-input
          clearable
          accept="application/pdf"
          ${this._form.field('curriculumVitae')}
          placeholder=${this._translation.translate('field', 'curriculumVitae')}
        ></tr-file-input>
      </tr-card>

      <tr-text bold>${this._translation.translate('signup_mentee', 'required_fields')}</tr-text>
      <tr-text>${this._translation.translate('signup_mentee', 'general_info')}</tr-text>
      <tr-text
        >${this._translation.translate('signup_mentee', 'questions_info')}
        <a href="mailto:mentoring@uni-frankfurt.de">mentoring@uni-frankfurt.de</a></tr-text
      >
    `;
  }

  protected override _fields(fields: BaseQLFieldDefinition[]) {
    return html`
      ${fields
        .filter(field => !userFieldBlackList.includes(field.name))
        .map(field => {
          const infoTranslation = this._translation.translate(this.descTranslKey, field.name);
          return (
            this.customFieldMapping?.(field, this._form, this._translation, this) ??
            html`
              <tr-flex ${css({ gridColumn: '1 / -1' })}>
                ${this._form.genericField([field.name], {
                  placeholderMode: 'static-floating',
                  info: infoTranslation !== `${this.descTranslKey}.${field.name}` ? infoTranslation : undefined,
                })}
              </tr-flex>
            `
          );
        })}
    `;
  }
}
